/**
 * pipelineNodeBuilder — builds PipelineNode[] for the PIPELINE CANVAS (workspace DAG).
 *
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │  This is the graph builder for the Pipeline Canvas (DAGCanvas).     │
 * │  It converts WorkspaceOperation[] + WorkspaceTable[] into           │
 * │  PipelineNode[] at TABLE-LEVEL granularity.                         │
 * │                                                                     │
 * │  The mapping canvas graph builder is buildCanvasGraph.ts in         │
 * │  ui/src/canvas/graph/ — a completely separate system.               │
 * │                                                                     │
 * │  See docs/CANVAS_ARCHITECTURE.md for the full distinction.          │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * Extracted from UnifiedCanvasLayout to reduce component complexity.
 */
import type { PipelineNode } from '../DAGCanvas';
import type { WorkspaceOperation, WorkspaceTable } from '../../../services/workspaceApi';
import type { TableValidationResult } from '../../../services/connectorApi';
import type { ProjectSource } from '../../../services/projectSourceApi';
import { normalizeTableKey, slugifyAlias } from '../../../canvas/utils/normalizeTableKey';
// ── Constants ────────────────────────────────────────────────────────

export const TRANSFORMATIVE_TYPES = new Set([
  'sql_transform',
  'python_transform',
  'fuzzy_match',
  'llm_extract',
  'llm_classify',
  'api_enrich',
  'validation',
  'schema_map',
  'hierarchical_assembly',
]);

/**
 * Short, user-facing display strings for operation_type. Rendered as
 * the small "kind" badge on the pipeline node so users see plain
 * language (the human-readable label is the node title).
 */
export const OPERATION_TYPE_DISPLAY: Record<string, string> = {
  sql_transform: 'SQL',
  python_transform: 'Python',
  fuzzy_match: 'Fuzzy match',
  llm_extract: 'LLM extract',
  llm_classify: 'LLM classify',
  api_enrich: 'API enrich',
  validation: 'Validation',
  schema_map: 'Map',
  hierarchical_assembly: 'Assembly',
};

/** Humanize an operation_type for display (fallback for unknown types). */
export function humanizeOperationType(opType: string): string {
  return OPERATION_TYPE_DISPLAY[opType] || opType.replace(/_/g, ' ');
}

// ── Helpers ──────────────────────────────────────────────────────────

/** Map operation status to DAG node status. */
export function mapOperationStatus(status: string): PipelineNode['status'] {
  switch (status) {
    case 'executed':
      return 'completed';
    case 'promoted':
      return 'completed';
    case 'corrected':
      return 'completed';
    case 'failed':
      return 'failed';
    default:
      return 'pending';
  }
}

/** Derive review result from operation review data. */
export function deriveReviewResult(
  review: WorkspaceOperation['review_result'],
): 'pass' | 'fixed' | 'warning' | null {
  if (!review) return null;
  if (review.corrections?.length > 0) return 'fixed';
  if (review.warnings?.length > 0) return 'warning';
  if (review.passed) return 'pass';
  return null;
}

/** Hide Excel workbook parent when per-sheet sources (stem__Sheet) exist. */
function withoutExcelParentSources(tables: WorkspaceTable[]): WorkspaceTable[] {
  const names = tables.map((t) => t.name);
  const canon = (n: string) => {
    let k = normalizeTableKey(n);
    while (k.includes('__')) k = k.replace(/__/g, '_');
    return k;
  };
  return tables.filter((t) => {
    if (!t.name || t.name.includes('__')) return true;
    const pk = canon(t.name);
    return !names.some(
      (c) => c !== t.name && canon(c).startsWith(`${pk}_`) && canon(c).length > pk.length + 1,
    );
  });
}

/**
 * Convert workspace tables + transformative operations to pipeline nodes.
 *
 * When source tables come from multiple origins (file uploads + connectors),
 * separate SourceGroupNode instances are created per origin. Otherwise a
 * single SourceGroupNode aggregates all source tables.
 * If workspace_meta.mapping_complete is set, a virtual MappingOutputNode
 * is appended after the last transform.
 * Excludes discarded operations.
 */
export function operationsToNodes(
  operations: WorkspaceOperation[],
  workspaceTables: WorkspaceTable[],
  batchId: string,
  batchFileName?: string,
  sessionMeta?: Record<string, any> | null,
  showExploration?: boolean,
  batchStatus?: string,
  batchMappingCount?: number,
  /** Target table names from mapping response (fallback when sessionMeta.target_tables not set). */
  mappingTargetTables?: string[],
  /** Per-target DDL and failure info from materialization. */
  materializationResult?: {
    viewDetails: Array<{ view_name: string; target_table: string; ddl: string; column_count: number }>;
    failedViews: Array<{ view: string; target: string; error: string; ddl: string; error_classification?: Record<string, any> }>;
  } | null,
  /** Whether the correction agent is currently running for failed views. */
  correctionInProgress?: boolean,
  /** Callback when user clicks a table inside TargetGroupNode. */
  onTargetTableClick?: (tableName: string, entry: { name: string; viewName: string; status: string; ddl?: string; error?: string }) => void,
  /** Callback when user clicks a source table for data preview. */
  onSourceTableClick?: (tableName: string) => void,
  /** Export validation results for Pipeline Output badge. */
  validationResults?: Map<string, TableValidationResult>,
  /** Quality summaries from review aggregation for Pipeline Output badge. */
  qualitySummaries?: Map<string, { overallStatus: 'pass' | 'warn' | 'fail' | 'no_review'; failedChecks: any[]; warnings: any[] }>,
  /** Project sources for origin grouping on the workspace canvas. */
  projectSources?: ProjectSource[],
  /** Mapping health data for per-target-table issue badges. */
  healthData?: { issues?: Array<{ target_table?: string; severity?: string }> } | null,
  /** User-curated set of output refs to surface as the Pipeline Output node. */
  selectedOutputRefs?: string[],
): { nodes: PipelineNode[]; allOutputToInputRefs: Map<string, string[]> } {
  const nodes: PipelineNode[] = [];

  // ── Source group nodes: one per origin when multiple origins exist ──
  // When tables come from different origins (file uploads vs connectors),
  // we create SEPARATE source group nodes so each origin is a distinct
  // visual block on the pipeline canvas.
  let sourceTables = workspaceTables.filter((t) => t.layer === 'source');

  // Use shared slugifyAlias: "CRO Source abc" → "cro-source-abc" (matches backend alias.slugify_alias)
  const slugify = slugifyAlias;

  // Build a lookup: slugified connector label → display info from project sources.
  // The pipeline node builder groups by the prefix before "::" in source file names,
  // which is the slugified connector label (e.g. "cro-source-abc").
  const connectorLabels = new Map<string, { label: string; connectorType: string | null }>();
  if (projectSources && projectSources.length > 0) {
    for (const ps of projectSources) {
      if (ps.source_type === 'connector' && ps.label && ps.connector_id) {
        const slug = slugify(ps.label);
        connectorLabels.set(slug, {
          label: ps.label,
          connectorType: (ps.metadata as any)?.connector_type ?? null,
        });
      }
    }
  }

  // Build a reverse lookup: bare table name → connector slug prefix.
  // This is used for grouping workspace tables by origin regardless of
  // whether their names contain the "::" prefix.  The import-from-connector
  // path stores bare names in DuckDB (e.g. "cro_conmed_snapshot") while the
  // scheduled rerun path may use namespaced names ("cro-source::cro_conmed_snapshot").
  // This lookup normalises both conventions.
  const tableToConnectorPrefix = new Map<string, string>();
  const activeStatuses = new Set(['profiled', 'active']);
  if (projectSources && projectSources.length > 0) {
    for (const ps of projectSources) {
      if (ps.source_type === 'connector' && activeStatuses.has(ps.status)) {
        const rawTables: Array<string | { name: string }> = (ps.metadata as any)?.tables ?? [];
        const prefix = slugify(ps.label ?? '');
        if (!prefix) continue;
        for (const tbl of rawTables) {
          const tblName = typeof tbl === 'string' ? tbl : tbl.name;
          // Map both bare name and namespaced name to the same prefix
          tableToConnectorPrefix.set(tblName, prefix);
          tableToConnectorPrefix.set(`${prefix}::${tblName}`, prefix);
        }
      }
    }
  }

  // Supplement workspace tables with connector tables not yet in workspace.
  // Only needed for sources whose tables genuinely don't appear in DuckDB
  // (e.g. connector unreachable). Avoid duplicates by checking bare name,
  // namespaced form, and sanitized stem (strips extension).
  // Tables that already have source_type='connector' from the registry
  // are already correctly grouped — no supplementary entry needed.
  const sourceTableNameSet = new Set(sourceTables.map((t) => t.name));
  // Also build a set of normalized stems for dedup against names with extensions
  const sourceTableStemSet = new Set(
    sourceTables.map((t) => t.name.replace(/\.(csv|tsv|json|parquet|xlsx|xls)$/i, '').toLowerCase()),
  );
  // When the registry has tagged workspace tables with source_type='connector',
  // we trust it as the authority and skip supplementary entries entirely.
  // This eliminates the duplicate 0×0 entries from project_sources metadata.
  const hasRegistryData = sourceTables.some((t) => (t as WorkspaceTable).source_type != null);

  if (!hasRegistryData && projectSources && projectSources.length > 0) {
    // Fallback for old batches without registry: add supplementary entries
    // for connector tables not yet in DuckDB.
    for (const ps of projectSources) {
      if (ps.source_type === 'connector' && activeStatuses.has(ps.status)) {
        const rawTables: Array<string | { name: string }> = (ps.metadata as any)?.tables ?? [];
        const prefix = slugify(ps.label ?? '');
        for (const tbl of rawTables) {
          const tblName = typeof tbl === 'string' ? tbl : tbl.name;
          const tblStem = tblName.replace(/\.(csv|tsv|json|parquet|xlsx|xls)$/i, '').toLowerCase();
          if (
            sourceTableNameSet.has(tblName) ||
            sourceTableNameSet.has(`${prefix}::${tblName}`) ||
            sourceTableStemSet.has(tblStem)
          ) {
            continue;
          }
          sourceTables.push({
            name: tblName,
            layer: 'source',
            row_count: 0,
            column_count: 0,
          } as any);
          sourceTableNameSet.add(tblName);
        }
      }
    }
  }

  sourceTables = withoutExcelParentSources(sourceTables);

  const sourceTableNames = sourceTables.map((t) => t.name);

  if (sourceTables.length > 0) {
    // Group source tables by origin. Use the tableToConnectorPrefix lookup
    // (built from projectSources metadata) which handles both bare and
    // namespaced table names. Fall back to "::" prefix detection for tables
    // not covered by projectSources.
    const groupMap = new Map<string, Array<{ name: string; displayName?: string; rowCount: number; columnCount: number }>>();
    for (const t of sourceTables) {
      let prefix: string | undefined;

      // Priority 1: Use registry source_type + connector_label (authoritative)
      const wt = t as WorkspaceTable;
      if (wt.source_type === 'connector' && wt.connector_label) {
        prefix = slugify(wt.connector_label);
      } else if (wt.source_type === 'file_upload') {
        prefix = '__file_uploads__';
      }

      // Priority 2: Fallback to name-based matching (old batches without registry)
      if (!prefix) {
        prefix = tableToConnectorPrefix.get(t.name);
      }
      if (!prefix) {
        const sepIdx = t.name.indexOf('::');
        prefix = sepIdx >= 0 ? t.name.slice(0, sepIdx) : '__file_uploads__';
      }

      if (!groupMap.has(prefix)) groupMap.set(prefix, []);
      groupMap.get(prefix)!.push({
        name: t.name,
        displayName: wt.display_name ?? undefined,
        rowCount: t.row_count ?? 0,
        columnCount: t.column_count ?? 0,
      });
    }

    const hasMultipleOrigins = groupMap.size > 1;

    if (hasMultipleOrigins) {
      // Create a separate source_group node for each origin
      groupMap.forEach((tables, prefix) => {
        const isFileUploads = prefix === '__file_uploads__';
        const info = connectorLabels.get(prefix);
        const label = isFileUploads ? 'File Uploads' : (info?.label ?? prefix);
        const connectorType = isFileUploads ? null : (info?.connectorType ?? null);
        const sanitizedPrefix = prefix.replace(/[^a-zA-Z0-9_-]/g, '_');
        const stepId = `__source_group__${sanitizedPrefix}__`;
        const groupRows = tables.reduce((sum, t) => sum + t.rowCount, 0);
        const groupTableNames = tables.map((t) => t.name);

        nodes.push({
          stepId,
          stepType: 'source_group',
          title: label,
          description: `${tables.length} table${tables.length !== 1 ? 's' : ''} · ${groupRows.toLocaleString()} rows`,
          status: 'completed',
          inputRefs: [],
          outputRef: stepId,
          _sourceTableNames: groupTableNames,
          _sourceTables: tables,
          _onSourceTableClick: onSourceTableClick,
          _originLabel: label,
          _connectorType: connectorType,
        } as PipelineNode);
      });
    } else {
      // Single origin — one source group node
      const totalRows = sourceTables.reduce((sum, t) => sum + (t.row_count || 0), 0);
      nodes.push({
        stepId: '__source_group__',
        stepType: 'source_group',
        title: 'Source Data',
        description: `${sourceTables.length} table${sourceTables.length !== 1 ? 's' : ''} · ${totalRows.toLocaleString()} rows`,
        status: 'completed',
        inputRefs: [],
        outputRef: '__source_group__',
        _sourceTableNames: sourceTableNames,
        _sourceTables: sourceTables.map((t) => ({
          name: t.name,
          displayName: t.display_name ?? undefined,
          rowCount: t.row_count,
          columnCount: t.column_count,
        })),
        _onSourceTableClick: onSourceTableClick,
      } as PipelineNode);
    }
  } else {
    // Fallback: session just started, no workspace tables yet
    const sourceLabel = batchFileName || batchId;
    nodes.push({
      stepId: '__source_group__',
      stepType: 'source_group',
      title: sourceLabel.length > 22 ? sourceLabel.slice(0, 20) + '…' : sourceLabel,
      description: 'Loading sources…',
      status: 'pending',
      inputRefs: [],
      outputRef: '__source_group__',
      _sourceTableNames: [],
      _sourceTables: [],
    });
  }

  // ── Build complete output→input mapping for ALL operations ─────
  // This is used by layoutDAG() to resolve edges through hidden
  // (exploratory) intermediate nodes so downstream nodes don't dangle.
  const allTransformOps = operations.filter(
    (op) => TRANSFORMATIVE_TYPES.has(op.operation_type) && op.status !== 'discarded',
  );
  const allOutputToInputRefs = new Map<string, string[]>();
  allTransformOps.forEach((op) => {
    if (op.output_ref) {
      allOutputToInputRefs.set(op.output_ref, op.input_refs || []);
    }
  });

  // Set of __mapped_X__ refs already produced by a real schema_map op.
  // Each schema_map op is the producer of __mapped_X__ in iterative mapping.
  // We do NOT render schema_map ops as their own visible nodes — instead,
  // their metadata is attached to the corresponding target_table node so
  // both mapping flows (V2 comprehensive and iterative) render identically:
  // source → mapping_output → target_table → [post-map] → [pipeline_output].
  //
  // Exploratory schema_map ops (the user marked them as not part of the
  // canonical pipeline) don't drive target_table rendering unless
  // showExploration is on — they remain in allTransformOps for edge
  // resolution but don't influence the canonical view of the workspace.
  const schemaMapOpByOutput = new Map<string, WorkspaceOperation>();
  for (const op of allTransformOps) {
    if (op.operation_type !== 'schema_map' || !op.output_ref) continue;
    if (op.exploratory && !showExploration) continue;
    // Last write wins — operations are chronologically ordered, so the
    // most recent schema_map for a given output supersedes earlier ones.
    schemaMapOpByOutput.set(op.output_ref, op);
  }

  // ── Transformative operation nodes ──────────────────────────────
  // Deduplicate: when the same output_ref appears multiple times (e.g.
  // from data-worker restarts replaying operations), keep only the latest.
  // Schema_map ops are excluded from this loop — they're surfaced via the
  // target_table nodes (rendered later in the mapping_complete block).
  const transformOps = allTransformOps.filter(
    (op) => (showExploration || !op.exploratory) && op.operation_type !== 'schema_map',
  );
  const latestByOutput = new Map<string, WorkspaceOperation>();
  transformOps.forEach((op) => {
    if (op.output_ref) {
      latestByOutput.set(op.output_ref, op); // last one wins (chronological)
    }
  });
  const deduped = transformOps.filter(
    (op) => !op.output_ref || latestByOutput.get(op.output_ref) === op,
  );

  // Helper: does this operation consume a __mapped_*__ view?
  // Post-map transforms belong downstream of target tables; their refs
  // resolve to the producing target_table node via outputToNode.
  // (Schema_map ops are filtered out above and don't reach this helper.)
  const isMappedDataTransform = (op: WorkspaceOperation): boolean =>
    (op.input_refs || []).some((ref) => ref.startsWith('__mapped_'));

  let lastTransformOutputRef: string | undefined;
  deduped.forEach((op) => {
    const outputTable = workspaceTables.find((t) => t.name === op.output_ref);
    const rawDesc = op.description?.replace('[SOLUTION] ', '') || '';
    const rawLabel = op.label?.trim() || '';
    if (!isMappedDataTransform(op)) {
      lastTransformOutputRef = op.output_ref || lastTransformOutputRef;
    }
    const rawCode = op.config?.sql || op.config?.function_code || op.config?.code || '';
    const kind = humanizeOperationType(op.operation_type);
    // Title priority: agent-supplied label (new) > output_ref > humanized kind.
    // We deliberately skip the legacy `description` field — it's an
    // auto-generated platform string like "Sql Transform -> subject_counts"
    // that surfaces internal jargon (CLAUDE.md guideline #6 forbids that).
    // The output_ref is the user-meaningful name the agent already picked
    // for the resulting table, so it makes a clean fallback when the label
    // hasn't been populated yet (pre-migration rows, or ops created by
    // looser earlier code paths).
    const nodeTitle = rawLabel || op.output_ref || kind;
    // Secondary line: show description only when it carries information
    // beyond the auto-generated "X Transform -> Y" pattern AND isn't the
    // same string as the title.
    const isAutoGeneratedDesc = /^(sql|python|fuzzy|llm|api)\s+\w+\s+->\s+/i.test(rawDesc);
    const nodeDescription = rawDesc && rawDesc !== nodeTitle && !isAutoGeneratedDesc ? rawDesc : undefined;
    nodes.push({
      stepId: op.operation_id,
      stepType: op.operation_type,
      kind,
      title: nodeTitle,
      description: nodeDescription,
      status: mapOperationStatus(op.status),
      rowCount: op.row_count_after ?? undefined,
      columnCount: outputTable?.column_count ?? undefined,
      cost: undefined,
      reviewResult: deriveReviewResult(op.review_result),
      reviewChecks: op.review_result?.checks,
      reviewCorrections: op.review_result?.corrections,
      inputRefs: op.input_refs || [],
      outputRef: op.output_ref || undefined,
      operationStatus: op.status,
      exploratory: op.exploratory || false,
      codeSnippet: rawCode || undefined,
    });
  });

  // ── Shared state for virtual nodes ──────────────────────────────
  const DONE_STATUSES = new Set(['executed', 'promoted', 'corrected', 'completed']);
  const sourceDataOps = deduped.filter((op) => !isMappedDataTransform(op));
  const hasCompletedTransform = sourceDataOps.some(
    (op) => !op.exploratory && DONE_STATUSES.has(op.status),
  );
  const hasSourceData = sourceTables.length > 0 || !!batchFileName;

  // Compute ALL terminal (unconsumed) transform output refs — these are what
  // the mapping node should connect from.
  let terminalTransformRefs: string[] = [];
  if (hasSourceData && hasCompletedTransform) {
    const consumedRefs = new Set<string>();
    sourceDataOps.forEach((op) => {
      if (!op.exploratory) {
        (op.input_refs || []).forEach((r) => consumedRefs.add(r));
      }
    });
    terminalTransformRefs = sourceDataOps
      .filter((op) => !op.exploratory && DONE_STATUSES.has(op.status) && op.output_ref && !consumedRefs.has(op.output_ref))
      .map((op) => op.output_ref!);
  }

  // Pre-mapping input refs: all terminal transform outputs, or source group(s) if none.
  const sourceGroupNodeIds = nodes
    .filter((n) => n.stepType === 'source_group')
    .map((n) => n.stepId);
  const preMappingInputRefs = terminalTransformRefs.length > 0
    ? terminalTransformRefs
    : lastTransformOutputRef
      ? [lastTransformOutputRef]
      : sourceGroupNodeIds.length > 0
        ? sourceGroupNodeIds
        : ['__source_group__'];

  // ── Virtual mapping output node (from workspace_meta) ──────────
  // Renders when a real target schema exists AND (mapping_complete OR schema_map ops exist
  // OR the batch has entered a mapping lifecycle status).
  // Skipped for storage-only exports (GCS/S3) where no target schema is defined —
  // those are pass-through exports with no meaningful mapping.
  // Source → Pre-Map Tx → Mapping Output → Target Tables → [Post-Map Tx] → [Pipeline Output] → Destination
  const hasSchemaMapOps = schemaMapOpByOutput.size > 0;
  const hasMappingLifecycleStatus = Boolean(
    batchStatus && ['mapping', 'mapped', 'approved', 'failed', 'completed'].includes(batchStatus),
  );
  const hasRealTargetSchema = Boolean(
    sessionMeta?.target_schema_id
    || (sessionMeta?.target_tables && sessionMeta.target_tables.length > 0)
    || hasSchemaMapOps
  );
  let mappingStatus: PipelineNode['status'] | undefined;
  if (hasRealTargetSchema && (sessionMeta?.mapping_complete || hasSchemaMapOps || hasMappingLifecycleStatus)) {
    const meta = sessionMeta ?? {};
    const mappingStatuses: Record<string, PipelineNode['status']> = {
      ready_for_mapping: 'pending',
      mapping: 'running',
      mapped: 'completed',
      approved: 'completed',
      failed: 'failed',
      completed: 'completed',
    };
    mappingStatus = (batchStatus && mappingStatuses[batchStatus]) || 'running';

    let mappingDesc = 'Generating Mappings\u2026';
    if (batchStatus === 'ready_for_mapping') mappingDesc = 'Awaiting Generation';
    else if (batchStatus === 'mapping') {
      const metaTargetCount = Array.isArray(meta.target_tables) ? meta.target_tables.length : 0;
      const mappedCount = Array.isArray(meta.mapped_tables) ? meta.mapped_tables.length : 0;
      if (
        meta.mapping_mode === 'iterative' &&
        mappedCount > 0 &&
        metaTargetCount > 0 &&
        mappedCount < metaTargetCount
      ) {
        mappingDesc = `${mappedCount}/${metaTargetCount} tables mapped`;
      } else {
        mappingDesc = 'Generating Mappings\u2026';
      }
    }
    else if (batchStatus === 'mapped') {
      if (batchMappingCount != null) {
        mappingDesc = `${batchMappingCount} mappings generated`;
      } else if (meta.mapped_tables?.length > 0) {
        // Agent-driven iterative mapping: derive count from mapped_tables
        mappingDesc = `${meta.mapped_tables.length} tables mapped`;
      } else if (meta.mapping_summary && !meta.mapping_summary.includes('Generating')) {
        mappingDesc = meta.mapping_summary;
      } else {
        mappingDesc = 'Mapping complete';
      }
    }
    else if (batchStatus === 'approved') mappingDesc = (meta.mapping_summary || 'Mapping') + ' \u00b7 Approved';

    let displayBatchStatus = batchStatus;
    if (meta.destination_status === 'exported' && mappingDesc.includes('Generating')) {
      mappingStatus = 'completed';
      displayBatchStatus = 'mapped';
      if (meta.mapped_tables?.length > 0) {
        mappingDesc = `${meta.mapped_tables.length} tables mapped`;
      } else if (batchMappingCount != null) {
        mappingDesc = `${batchMappingCount} mappings generated`;
      } else {
        mappingDesc = 'Mapping complete';
      }
    }

    // Build target table list — union of:
    //   1. mappingTargetTables — V2 flow's actual recipe targets
    //   2. sessionMeta.target_tables — user-declared targets from schema picker
    //   3. schema_map ops' config.target_schema_name — iterative flow targets
    const metaTables = meta.target_tables;
    const baseTargets: string[] = (mappingTargetTables && mappingTargetTables.length > 0)
      ? mappingTargetTables
      : (metaTables ?? []);
    const seenLower = new Set<string>();
    const targetTables: string[] = [];
    for (const t of baseTargets) {
      const k = t.toLowerCase();
      if (!seenLower.has(k)) { seenLower.add(k); targetTables.push(t); }
    }
    for (const op of allTransformOps) {
      if (op.operation_type !== 'schema_map') continue;
      const tname = (op.config as any)?.target_schema_name;
      if (!tname || typeof tname !== 'string') continue;
      const k = tname.toLowerCase();
      if (!seenLower.has(k)) { seenLower.add(k); targetTables.push(tname); }
    }

    // Build per-table lookups from materialization result (V2 path)
    const ddlMap = new Map(
      (materializationResult?.viewDetails ?? []).map(v => [v.target_table, v.ddl]),
    );
    const failedMap = new Map(
      (materializationResult?.failedViews ?? []).map(f => [f.target, f]),
    );
    const createdSet = new Set(
      (materializationResult?.viewDetails ?? []).map(v => v.target_table),
    );

    // Resolve each target table's status/code/row count from whichever
    // path produced it. Same priority as before:
    //   1. schema_map operation (iterative)  2. materialization result (V2)
    //   3. workspace table row count          4. pending
    const resolveTarget = (tableName: string) => {
      const sanitized = tableName.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      const viewName = `__mapped_${sanitized}__`;
      const op = schemaMapOpByOutput.get(viewName);
      const failedEntry = failedMap.get(tableName);
      const isCreated = createdSet.has(tableName);
      const wt = workspaceTables.find((t) => t.name === viewName);

      let status: PipelineNode['status'] = 'pending';
      let codeSnippet: string | undefined;
      let error: string | undefined;
      let rowCount: number | undefined;
      let columnCount: number | undefined;
      let opId: string | undefined;

      if (op) {
        opId = op.operation_id;
        status = mapOperationStatus(op.status);
        codeSnippet = (op.config as any)?.sql || undefined;
        rowCount = op.row_count_after ?? wt?.row_count ?? undefined;
        columnCount = wt?.column_count ?? undefined;
      } else if (materializationResult) {
        if (failedEntry && correctionInProgress) {
          status = 'running';
        } else if (failedEntry) {
          status = 'failed';
          error = failedEntry.error;
        } else if (isCreated) {
          status = 'completed';
        }
        codeSnippet = ddlMap.get(tableName) || failedEntry?.ddl;
        rowCount = wt?.row_count ?? undefined;
        columnCount = wt?.column_count ?? undefined;
      } else if (wt) {
        status = 'completed';
        rowCount = wt.row_count ?? undefined;
        columnCount = wt.column_count ?? undefined;
      }

      // Batch-level completion can arrive before per-table signals (materialized
      // views, schema_map ops, or workspace table refresh). Without this, the
      // container shows a checkmark from batchStatus but stale "0/N mapped".
      if (
        status === 'pending' &&
        (displayBatchStatus === 'mapped' || displayBatchStatus === 'approved')
      ) {
        status = 'completed';
      }

      return { sanitized, viewName, status, codeSnippet, error, rowCount, columnCount, opId, failedEntry, isCreated };
    };

    // Build the per-table entries the container will render in its body.
    // Mirrors TargetGroupNode's table shape so the existing inspector callback
    // (onTargetTableClick) keeps working without changes downstream.
    const containerTables = targetTables.map((tableName) => {
      const r = resolveTarget(tableName);
      const entryHealthIssues = healthData?.issues?.filter(
        (hi) => hi.target_table?.toLowerCase() === tableName.toLowerCase(),
      ) || [];
      const entryHealthCount = entryHealthIssues.length;
      const entryHasErrors = entryHealthIssues.some((hi) => hi.severity === 'error');
      return {
        name: tableName,
        viewName: r.viewName,
        status: r.status,
        ddl: r.codeSnippet,
        error: r.error,
        healthIssueCount: entryHealthCount > 0 ? entryHealthCount : undefined,
        healthSeverity:
          entryHealthCount > 0 ? (entryHasErrors ? ('error' as const) : ('warning' as const)) : undefined,
      };
    });

    // All per-table view names (__mapped_X__) — registered as additional
    // outputRefs of the container so downstream consumers (post-map transforms,
    // hierarchical_assembly) resolve their edges to the container instead of
    // dangling. Without this, the old per-table nodes' edge endpoints would
    // become orphaned when those nodes stop being emitted.
    const containerViewNames = containerTables.map((t) => t.viewName);

    // Also keep the lineage trace: __mapped_X__ → __target_schema__, so
    // hidden ops (exploratory, fuzzy_match, etc.) resolve through the chain.
    for (const viewName of containerViewNames) {
      if (!allOutputToInputRefs.has(viewName)) {
        allOutputToInputRefs.set(viewName, ['__target_schema__']);
      }
    }

    nodes.push({
      stepId: '__target_schema__',
      stepType: 'target_schema_container',
      title: meta.target_schema_name || 'Target Schema',
      description: mappingDesc,
      status: mappingStatus,
      inputRefs: preMappingInputRefs,
      outputRef: '__target_schema__',
      _batchId: batchId,
      _batchStatus: displayBatchStatus,
      _mappingMode: meta.mapping_mode,
      _targetTables: containerTables,
      _additionalOutputRefs: containerViewNames,
      _onTableClick: (tableName: string) => {
        const entry = containerTables.find((t) => t.name === tableName);
        if (entry) {
          onTargetTableClick?.(tableName, entry);
        }
      },
    } as PipelineNode);
  }

  // ── Virtual pipeline output node (selection-driven) ─────────────
  // Only rendered when the user has explicitly curated a set of output
  // refs via the Outputs drawer (persisted to workspace_meta.selected_output_refs).
  // Without a selection, the DAG ends at target tables / post-map transforms
  // / assembly — there is no synthetic "pipeline output" node.
  const validatedSelection: string[] = [];
  if (hasSourceData && Array.isArray(selectedOutputRefs) && selectedOutputRefs.length > 0) {
    // Build the set of refs known to be produced by something on the canvas.
    // Drops dead refs so Pipeline Output never points at phantoms.
    // Note: hierarchical_assembly nodes are added later by mergeWithPipelineState
    // and don't appear in `nodes` here, so any "assembly://" ref is treated
    // as inherently valid.
    const knownOutputs = new Set<string>();
    nodes.forEach((n) => { if (n.outputRef) knownOutputs.add(n.outputRef); });
    allOutputToInputRefs.forEach((_v, k) => knownOutputs.add(k));
    for (const tableName of (mappingTargetTables || sessionMeta?.target_tables || [])) {
      const sanitized = String(tableName).toLowerCase().replace(/[^a-z0-9_]/g, '_');
      knownOutputs.add(`__mapped_${sanitized}__`);
    }
    selectedOutputRefs.forEach((ref) => {
      const isAssembly = typeof ref === 'string' && ref.startsWith('assembly://');
      if ((isAssembly || knownOutputs.has(ref)) && !validatedSelection.includes(ref)) {
        validatedSelection.push(ref);
      }
    });
  }

  if (validatedSelection.length > 0) {
    const failedViewCount = materializationResult?.failedViews?.length ?? 0;

    // Aggregate row count only for purely-tabular selections. Mixing in
    // an assembly:// ref makes a row total meaningless, so leave it
    // undefined in that case rather than display a misleading number.
    const hasAssemblyRef = validatedSelection.some((r) => r.startsWith('assembly://'));
    let totalRows = 0;
    let knownRows = false;
    if (!hasAssemblyRef) {
      for (const ref of validatedSelection) {
        const wt = workspaceTables.find((t) => t.name === ref);
        if (wt?.row_count != null) {
          totalRows += wt.row_count;
          knownRows = true;
        }
      }
    }

    // Derive validation badge from validationResults param
    let validationStatus: 'pass' | 'warn' | 'error' | undefined;
    let validationCount = 0;
    if (validationResults && validationResults.size > 0) {
      const results = Array.from(validationResults.values());
      const hasError = results.some((r) => r.status === 'fail');
      const hasWarn = results.some((r) => r.status === 'warn');
      validationStatus = hasError ? 'error' : hasWarn ? 'warn' : 'pass';
      validationCount = results.reduce((sum, r) => sum + r.issues.filter((i) => i.severity !== 'ok').length, 0);
    }

    // Derive quality badge from qualitySummaries param
    let qualityStatus: 'pass' | 'warn' | 'fail' | 'no_review' | undefined;
    let qualityIssueCount = 0;
    if (qualitySummaries && qualitySummaries.size > 0) {
      const summaries = Array.from(qualitySummaries.values());
      const hasFail = summaries.some((s) => s.overallStatus === 'fail');
      const hasWarn = summaries.some((s) => s.overallStatus === 'warn');
      const hasNoReview = summaries.some((s) => s.overallStatus === 'no_review');
      qualityStatus = hasFail ? 'fail' : hasWarn ? 'warn' : hasNoReview ? 'no_review' : 'pass';
      qualityIssueCount = summaries.reduce((sum, s) => sum + s.failedChecks.length + s.warnings.length, 0);
    }

    nodes.push({
      stepId: '__pipeline_output__',
      stepType: 'pipeline_output',
      title: 'Pipeline Output',
      description: `${validatedSelection.length} selected output${validatedSelection.length !== 1 ? 's' : ''}`,
      status: 'completed',
      rowCount: knownRows ? totalRows : undefined,
      inputRefs: validatedSelection,
      outputRef: '__pipeline_output__',
      _failedViewCount: failedViewCount,
      _validationStatus: validationStatus,
      _validationCount: validationCount,
      _qualityStatus: qualityStatus,
      _qualityIssueCount: qualityIssueCount,
    } as PipelineNode);
  }

  // ── Virtual destination node (from workspace_meta) ─────────────
  // Only rendered when both a destination connector is configured AND a
  // Pipeline Output exists (i.e. the user has curated outputs). This keeps
  // the DAG narrative clear: pick outputs first, then attach a destination.
  const pipelineOutputExists = nodes.some((n) => n.stepId === '__pipeline_output__');
  if (sessionMeta?.destination_connector_id && pipelineOutputExists) {
    const destStatusRaw = sessionMeta.destination_status || 'pending';
    const destNodeStatus: PipelineNode['status'] =
      destStatusRaw === 'exported' ? 'completed'
        : destStatusRaw === 'exporting' ? 'running'
        : destStatusRaw === 'failed' ? 'failed'
        : 'pending';

    nodes.push({
      stepId: '__destination__',
      stepType: 'destination',
      title: sessionMeta.destination_connector_name || 'Destination',
      status: destNodeStatus,
      rowCount: sessionMeta.destination_rows_written,
      inputRefs: ['__pipeline_output__'],
      _connectorType: sessionMeta.destination_connector_type,
      _targetTable: sessionMeta.destination_table,
      _destinationStatus: sessionMeta.destination_status || 'pending',
      _destinationExportedAt: sessionMeta.destination_exported_at,
      _destinationError: sessionMeta.destination_error,
    });
  }

  return { nodes, allOutputToInputRefs };
}

/** Merge SSE pipeline state into operation-derived nodes.
 *
 * Also materializes a PipelineNode for any pipeline step that has no
 * corresponding workspace operation — currently `hierarchical_assembly`
 * steps, which are auto-inserted by the pipeline handler with
 * `source_operation_id=null` and never appear in `operations`.
 */
export function mergeWithPipelineState(
  opNodes: PipelineNode[],
  pipelineState: {
    steps: Array<{
      step_id: string;
      status: string;
      step_type: string;
      title: string;
      output_ref?: string;
      input_refs?: string[];
      actual_cost?: number;
      row_count?: number;
      metrics?: Record<string, any>;
      error?: string;
    }>;
  } | null,
): PipelineNode[] {
  if (!pipelineState?.steps?.length) return opNodes;

  const stepMap = new Map(pipelineState.steps.map((s) => [s.step_id, s]));
  const opNodeIds = new Set(opNodes.map((n) => n.stepId));

  const merged = opNodes.map((node) => {
    const step = stepMap.get(node.stepId);
    if (step) {
      return {
        ...node,
        status: (step.status || node.status) as PipelineNode['status'],
        cost: step.actual_cost ?? node.cost,
        outputRef: step.output_ref || node.outputRef,
      };
    }
    return node;
  });

  // Materialize PipelineNodes for assembly steps with no source operation.
  // Insert each one after the last node it depends on (last input_ref match),
  // or at the end if no upstream node is found.
  for (const step of pipelineState.steps) {
    if (step.step_type !== 'hierarchical_assembly') continue;
    if (opNodeIds.has(step.step_id)) continue;

    const inputRefs = step.input_refs || [];
    const metrics = step.metrics || {};
    const outputFormat =
      metrics.output_format ||
      (step.output_ref ? step.output_ref.split('.').pop() : undefined);

    const assemblyNode: PipelineNode = {
      stepId: step.step_id,
      stepType: 'hierarchical_assembly',
      title: step.title || 'Hierarchical Assembly',
      status: (step.status || 'pending') as PipelineNode['status'],
      inputRefs,
      outputRef: step.output_ref,
      rowCount: step.row_count,
      _assemblyOutputFormat: outputFormat,
      _assemblyByteSize: typeof metrics.byte_size === 'number' ? metrics.byte_size : undefined,
      _assemblyError: step.error,
    } as PipelineNode;

    // Insert after the last upstream node (matched by outputRef ∈ inputRefs);
    // fallback to end of list.
    let insertIdx = merged.length;
    for (let i = merged.length - 1; i >= 0; i--) {
      if (merged[i].outputRef && inputRefs.includes(merged[i].outputRef!)) {
        insertIdx = i + 1;
        break;
      }
    }
    merged.splice(insertIdx, 0, assemblyNode);
  }

  return merged;
}
